import { formatDateTime } from '../../lib/DateTime.js';
import {
    useBusinessInfo,
    useCalendarEventsQuery,
    useCalendarViewDetailsQuery,
} from '../../lib/Queries';
import { useEffect, useRef, useState } from 'react';
import {
    addDays, differenceInDays,
    differenceInMinutes,
    endOfDay,
    endOfMonth,
    startOfDay,
    startOfMonth
} from 'date-fns';
import Loading from '../../components/Loading';
import BigCalendar from '../../components/calendar/BigCalendar.jsx';
import MiniCal from '../../components/calendar/MiniCal.jsx';
import EventDetailsPanel from '../../components/calendar/EventDetailsPanel';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import SchedulingPanel from '../../components/scheduling/SchedulingPanel.jsx';
import ReschedulingPanel from '../../components/scheduling/ReschedulingPanel.jsx';
import reschedulingPanel from '../../components/scheduling/ReschedulingPanel.jsx';
import ClassDetailsPanel from '../../components/calendar/ClassDetailsPanel.jsx';
import ViewTimeZone from '../../components/calendar/ViewTimeZone.jsx';
import CalendarFilterBookableResources, {
    filterEvents,
} from '~/components/calendar/CalendarFilterBookableResources';
import BlockTimePanel from '~/components/calendar/BlockTimePanel.jsx';
import useEventColors from '~/lib/useEventColors';
import useSelectedEvent from '~/lib/useSelectedEvent';

const CalendarPage = ({}) => {
    const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
    const [viewStartDate, setViewStartDate] = useState(selectedDate);
    const [viewEndDate, setViewEndDate] = useState(selectedDate);

    const [currentFilter, setCurrentFilter] = useState([]);
    const [resourceFilter, setResourceFilter] = useState();

    const [isSchedulingOpen, setIsSchedulingOpen] = useState(false);
    const [isBlockingOpen, setIsBlockingOpen] = useState(false);
    const [selectedStartEnd, setSelectedStartEnd] = useState(null);
    const [selectedResourceId, setSelectedResourceId] = useState(null);
    const [selectedBlockedTime, setSelectedBlockedTime] = useState(null);
    const [schedulingClient, setSchedulingClient] = useState(null);
    const [schedulingEventTypeId, setSchedulingEventTypeId] = useState(null);

    const [reschedulingEvent, setReschedulingEvent] = useState(null);
    const [reschedulingStart, setReschedulingStart] = useState(null);

    const { data: businessInfoData } = useBusinessInfo();

    let eventsStart = viewStartDate;
    let eventsEnd = viewEndDate;
    if (differenceInDays(viewEndDate, viewStartDate) < 30) {
        eventsStart = startOfMonth(viewStartDate);
        eventsEnd = endOfMonth(viewEndDate);
    }

    const { isLoading, data, refetch } = useCalendarEventsQuery(eventsStart, eventsEnd);

    const { data: viewDetails } = useCalendarViewDetailsQuery(
        viewStartDate,
        viewEndDate,
    );

    const { selectEvent, showScheduledEvent, detailsProps } =
        useSelectedEvent();
    const [selectedClass, setSelectedClass] = useState();

    const { colorBasedOn } = useEventColors();

    const containerRef = useRef();

    useEffect(() => {
        const params = new URL(document.location).searchParams;
        if (params.get('eventId')) {
            selectEvent({
                id: parseInt(params.get('eventId')),
            });
        }
    }, [selectEvent]);

    const events = filterEvents(currentFilter, data || [], colorBasedOn);
    const viewingBookableResource = (
        viewDetails?.bookableResources || []
    ).filter(
        br =>
            !resourceFilter ||
            resourceFilter.indexOf(br.id) > -1,
    );

    function onDragEnd({
        event,
        resource,
        newClientStart,
        newClientEnd,
        revert,
    }) {
        setReschedulingEvent(event);
        setSelectedResourceId(resource?.id);
        setReschedulingStart(newClientStart);
    }

    function newEvent() {
        setSelectedStartEnd(null);
        setIsSchedulingOpen(true);
    }

    return (
        <div className="h-screen" ref={containerRef}>
            {isLoading && (
                <div className="absolute z-40 top-[50%] left-[50%] bg-white px-4 py-2 rounded-full shadow-lg">
                    <Loading />
                </div>
            )}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_340px] gap-4">
                <div className="order-2 md:order-1 bg-white rounded-lg p-4 flex-1 w-full">
                    <BigCalendar
                        setSelectedDate={setSelectedDate}
                        selectedDate={selectedDate}
                        weekStartsOn={businessInfoData?.business?.week_start}
                        use24HourTime={
                            businessInfoData?.business?.use_24_hour_time
                        }
                        setViewStartDate={setViewStartDate}
                        setViewEndDate={setViewEndDate}
                        onEventClicked={ev => {
                            selectEvent(ev);
                        }}
                        onClassClicked={ev => {
                            setSelectedClass(ev);
                        }}
                        onBlockedClicked={ev => {
                            setIsBlockingOpen(true);
                            setSelectedBlockedTime(ev);
                        }}
                        onClickAdd={newEvent}
                        events={events}
                        hours={viewDetails?.hours}
                        bookableResources={viewingBookableResource}
                        height={Math.max(
                            containerRef.current?.offsetHeight - 64,
                            400,
                        )}
                        onDragEnd={onDragEnd}
                        onSelectTime={({ start, resource, end }) => {
                            setSelectedResourceId(resource?.id);
                            setSelectedStartEnd({ start, end });
                            setSelectedBlockedTime(null);

                            const duration = differenceInMinutes(end, start);
                            if (duration <= 15) {
                                setIsSchedulingOpen(true);
                            } else {
                                setIsBlockingOpen(true);
                            }
                        }}
                    />
                </div>

                <div className="w-full order-1 md:order-2">
                    <div className="panel px-1 mb-2 !max-w-full">
                        <MiniCal
                            selectedDate={selectedDate}
                            onSelectDay={setSelectedDate}
                            viewStart={viewStartDate}
                            viewEnd={viewEndDate}
                            events={events}
                            weekStart={businessInfoData?.business?.week_start}
                            className='w-full'
                            calendarClassName='!w-full'
                        />
                    </div>

                    <div className="flex items-center justify-between my-4">
                        <button
                            type="button"
                            onClick={newEvent}
                            className="btn-pill mr-4"
                        >
                            Schedule Appointment
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                setSelectedBlockedTime(null);
                                setIsBlockingOpen(true);
                            }}
                            className="btn-light-pill"
                        >
                            Block Time
                        </button>
                    </div>

                    <ViewTimeZone
                        bookableResources={viewDetails?.bookableResources}
                        className="mb-4 text-gray-500 text-xs max-w-64 text-center "
                    />

                    <CalendarFilterBookableResources
                        onFilterChange={setCurrentFilter}
                        onResourceFilterChange={setResourceFilter}
                        className="my-6 w-full"
                    />
                </div>
            </div>
            <EventDetailsPanel
                {...detailsProps}
                onEventChanged={refetch}
                onBookAgain={(client, eventTypeId) => {
                    selectEvent(null);
                    setSchedulingClient(client ? {
                        email: client.email,
                        firstName: client.first_name,
                        lastName: client.last_name,
                    } : null);
                    setSchedulingEventTypeId(eventTypeId);
                    setSelectedStartEnd(null);
                    setIsSchedulingOpen(true);
                }}
            />
            <ClassDetailsPanel
                selectedClass={selectedClass}
                isOpen={!!selectedClass}
                onClose={() => setSelectedClass(null)}
                onClassUpdated={refetch}
            />
            <SchedulingPanel
                isOpen={isSchedulingOpen}
                onClose={() => {
                    setIsSchedulingOpen(false);
                    setSchedulingClient(null);
                    setSchedulingEventTypeId(null);
                }}
                start={selectedStartEnd?.start}
                resourceId={selectedResourceId}
                client={schedulingClient}
                eventTypeId={schedulingEventTypeId}
                onScheduled={events => {
                    refetch();
                    showScheduledEvent(events);
                }}
            />
            <BlockTimePanel
                isOpen={isBlockingOpen}
                onClose={() => setIsBlockingOpen(false)}
                start={selectedStartEnd?.start}
                end={selectedStartEnd?.end}
                resourceId={selectedResourceId}
                onSuccess={refetch}
                block={selectedBlockedTime}
            />
            <ReschedulingPanel
                isOpen={!!reschedulingEvent}
                onClose={() => setReschedulingEvent(null)}
                event={reschedulingEvent}
                resourceId={selectedResourceId}
                onRescheduled={refetch}
                start={reschedulingStart}
            />
        </div>
    );
};

export default CalendarPage;
